import React, { ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { clamp, throttle } from 'lodash';
import { Grid, GridGapSizes } from '../grid/Grid';
import styles from './GridWithCulling.module.scss';

export class GridWithCulling extends React.Component<Props> {

    container: HTMLElement | null = null;
    containerElement: HTMLElement | null = null;

    cullingContainer: HTMLElement | null = null;
    cullingContainerElement: HTMLElement | null = null;

    computedView: { height: number; top: number } = {
        height: 0,
        top: 0
    };

    onScroll: (target: EventTarget) => void = throttle(target => {
        this.props.onScroll(target.scrollTop);
    }, 100, { leading: false });

    cellHeight: number = this.props.expectedCellHeight;

    componentDidMount = () => {

        this.containerElement = this.findContainerDOMNode()!;
        this.cullingContainerElement = this.findCullingContainerDOMNode();
        this.containerElement.scrollTop = this.props.scroll;

        const previousCellHeight = this.cellHeight;

        this.calculateCellHeight();

        if (previousCellHeight !== this.cellHeight) {
            this.forceUpdate();
            this.containerElement.scrollTop = this.props.scroll;
        }

        this.keepScrollWithinBounds();
    };

    componentDidUpdate = () => {
        this.containerElement!.scrollTop = this.props.scroll;
        this.calculateCellHeight();
        this.computeView();
        this.keepHeightInSync();
        this.keepScrollWithinBounds();
    };

    findContainerDOMNode = () => ReactDOM.findDOMNode(this.container) as HTMLElement;

    findCullingContainerDOMNode = () => ReactDOM.findDOMNode(this.cullingContainer) as HTMLElement;

    /**
     * Scrolls back up if the number of elements has been reduced.
     */
    keepScrollWithinBounds = () => {

        const container = this.findContainerDOMNode();

        if (container.scrollTop > this.measureAllRows()) {
            container.scrollTop = this.measureCulledRows();
        }
    };

    /**
     * When increasing / decreasing gridSize OR when initializing grid, height of grid can get out of sync.
     */
    keepHeightInSync = () => {

        const container = this.findCullingContainerDOMNode();
        const supposedHeight = Math.ceil(this.computedView.height);

        if (container.clientHeight !== supposedHeight) {
            container.style.height = `${supposedHeight}px`;
        }
    };

    cullCells = (): ReactNode[] => {

        const startIndex = this.countCulledCells();

        const cellLimit = this.getCellLimit();

        return this
            .flattenCells()
            .slice(startIndex, startIndex + cellLimit);
    };

    getCellLimit = () => {

        const container = this.containerElement;

        if (!container) {
            return 50;
        }

        const scrollSafetyFactor = 2.5;

        const rowsInView = Math.ceil(container.clientHeight / this.cellHeight);
        const targetCellLimit = Math.ceil((rowsInView * this.props.columns) * scrollSafetyFactor);

        return clamp(targetCellLimit, 25, 160);
    };

    calculateCellHeight = () => {
        this.cellHeight = this.containerElement
            ? this.containerElement.getBoundingClientRect().width / this.props.columns
            : this.props.expectedCellHeight;
    };

    countCulledCells = () => this.countCulledRows() * this.props.columns;

    countCulledRows = () => {
        const passedRows = Math.floor(this.props.scroll / this.cellHeight);
        return Math.max(0, passedRows - 3);
    };

    measureCulledRows = () => {
        return this.countCulledRows() * this.cellHeight;
    };

    measureAllRows = () => {

        const cellCount = this.flattenCells().length;

        const remainder = cellCount % this.props.columns;

        const flattenedCellCount = remainder > 0
            ? cellCount + (this.props.columns - remainder)
            : cellCount;

        const flattenedRowCount = flattenedCellCount / this.props.columns;

        return flattenedRowCount * this.cellHeight;
    };

    flattenCells = () => {
        return this.props.children.flatMap(c => c);
    };

    /**
     * Computes culling cell holder view
     */
    computeView = () => {

        const offset = this.measureCulledRows();
        const size = this.measureAllRows() - offset;

        this.computedView = { height: size, top: offset };
    };

    render = () => {
        this.computeView();
        return (
            <div className={styles.container}
                onScroll={e => this.onScroll(e.target)}
                ref={e => this.container = e}>
                <div className={styles.view} style={this.computedView} ref={e => this.cullingContainer = e}>
                    <Grid gap={this.props.gap} xs={templateForColumnCount(this.props.columns)} wrap>
                        {this.cullCells()}
                    </Grid>
                </div>
            </div>
        );
    };
}

type Props = {
    gap: GridGapSizes;
    columns: ColumnCount;
    expectedCellHeight: number;
    scroll: number;
    onScroll: (scroll: number) => void;
    children: ReactNode[];
};

type ColumnCount = 2 | 3 | 4 | 5 | 6;

const templateForColumnCount = <TColumnCount extends ColumnCount>(
    columns: TColumnCount
): `1/${TColumnCount}` => {
    return `1/${columns}`;
};